import * as THREE from 'three';
import { ASSET_TYPE } from '@src/components/scene/react-three-components/sceneAsset';

export function calculateSceneBounds(
  sceneAssets: any[],
  selectableAssets: any[]
): number[] {
  const allAssets = [...sceneAssets, ...selectableAssets];

  let minX = Infinity;
  let minY = Infinity;
  let minZ = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let maxZ = -Infinity;

  let hasValidAssets = false;

  const box = new THREE.Box3();

  for (const asset of allAssets) {
    const sceneAsset = asset.props.asset;

    if (shouldSkipAssetForBounds(sceneAsset)) {
      continue;
    }

    const threeObj = sceneAsset.ref?.current;
    if (!threeObj) {
      continue;
    }

    try {
      const assetBounds = getAssetBoundingBox(threeObj, sceneAsset, box);
      if (assetBounds) {
        const [
          assetMinX,
          assetMinY,
          assetMinZ,
          assetMaxX,
          assetMaxY,
          assetMaxZ,
        ] = assetBounds;

        minX = Math.min(minX, assetMinX);
        minY = Math.min(minY, assetMinY);
        minZ = Math.min(minZ, assetMinZ);
        maxX = Math.max(maxX, assetMaxX);
        maxY = Math.max(maxY, assetMaxY);
        maxZ = Math.max(maxZ, assetMaxZ);

        hasValidAssets = true;
      }
    } catch (error) {
      console.warn(
        `Error calculating bounds for asset ${sceneAsset.name}:`,
        error
      );
    }
  }

  if (!hasValidAssets) {
    return [-50, -50, -50, 50, 50, 50];
  }

  const sizeX = maxX - minX;
  const sizeY = maxY - minY;
  const sizeZ = maxZ - minZ;

  const paddingX = Math.max(sizeX * 0.1, 5);
  const paddingY = Math.max(sizeY * 0.1, 5);
  const paddingZ = Math.max(sizeZ * 0.1, 5);

  return [
    minX - paddingX,
    minY - paddingY,
    minZ - paddingZ,
    maxX + paddingX,
    maxY + paddingY,
    maxZ + paddingZ,
  ];
}

function shouldSkipAssetForBounds(sceneAsset: any): boolean {
  if (sceneAsset.type === ASSET_TYPE.PRIMITIVE.POSITION_POINT) {
    return true;
  }

  if (sceneAsset.type === ASSET_TYPE.MISC.LIGHT) {
    return true;
  }

  if (sceneAsset.type === ASSET_TYPE.MISC.POSITIONAL_AUDIO) {
    return true;
  }

  return false;
}

function getAssetBoundingBox(
  threeObj: THREE.Object3D,
  sceneAsset: any,
  box: THREE.Box3
): number[] | null {
  if (!threeObj) {
    return null;
  }

  switch (sceneAsset.type) {
    case ASSET_TYPE.PRIMITIVE.BOX:
      return getPrimitiveBounds(threeObj, 'box');

    case ASSET_TYPE.PRIMITIVE.SPHERE:
      return getPrimitiveBounds(threeObj, 'sphere');

    case ASSET_TYPE.PRIMITIVE_TEXTURE.IMAGE_PLANE:
    case ASSET_TYPE.PRIMITIVE_TEXTURE.VIDEO_PLANE:
    case ASSET_TYPE.PRIMITIVE_TEXTURE.SCREEN_SHARE:
    case ASSET_TYPE.PRIMITIVE_TEXTURE.STREAM:
    case ASSET_TYPE.PRIMITIVE_TEXTURE.SVG_PLANE:
      return getPrimitiveBounds(threeObj, 'plane');

    default:
      return getModelBounds(threeObj, box);
  }
}

function getPrimitiveBounds(
  threeObj: THREE.Object3D,
  primitiveType: string
): number[] {
  const position = threeObj.position;
  const scale = threeObj.scale;

  let halfSizeX, halfSizeY, halfSizeZ;

  switch (primitiveType) {
    case 'box':
      halfSizeX = scale.x * 0.5;
      halfSizeY = scale.y * 0.5;
      halfSizeZ = scale.z * 0.5;
      break;

    case 'sphere':
      const radius = Math.max(scale.x, scale.y, scale.z) * 0.5;
      halfSizeX = halfSizeY = halfSizeZ = radius;
      break;

    case 'plane':
      halfSizeX = scale.x * 0.5;
      halfSizeY = scale.y * 0.5;
      halfSizeZ = 0.1;
      break;

    default:
      halfSizeX = halfSizeY = halfSizeZ = 1;
  }

  return [
    position.x - halfSizeX,
    position.y - halfSizeY,
    position.z - halfSizeZ,
    position.x + halfSizeX,
    position.y + halfSizeY,
    position.z + halfSizeZ,
  ];
}

function getModelBounds(
  threeObj: THREE.Object3D,
  box: THREE.Box3
): number[] | null {
  try {
    if (threeObj.matrixWorldNeedsUpdate) {
      threeObj.updateMatrixWorld(true);
    }

    box.makeEmpty();
    box.setFromObject(threeObj);

    if (box.isEmpty()) {
      const position = threeObj.position;
      const scale = threeObj.scale;
      const fallbackSize = Math.max(scale.x, scale.y, scale.z);

      return [
        position.x - fallbackSize,
        position.y - fallbackSize,
        position.z - fallbackSize,
        position.x + fallbackSize,
        position.y + fallbackSize,
        position.z + fallbackSize,
      ];
    }

    return [box.min.x, box.min.y, box.min.z, box.max.x, box.max.y, box.max.z];
  } catch (error) {
    console.warn('Error calculating model bounds:', error);
    return null;
  }
}
